export default function PrivacyPage() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <h1 className="text-2xl font-bold mb-4">Privacy Policy</h1>
      <p className="text-sm text-gray-500 mb-8">Last updated: February 21, 2026</p>

      <p className="text-gray-700 leading-relaxed mb-4">
        Echonour ("we", "us", "our") respects your privacy. This policy explains what data we collect, how we use it, and your rights. We've kept it readable — no walls of legal text.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-3">What We Collect</h2>
      <p className="text-gray-700 leading-relaxed mb-4">When you use Echonour, we collect:</p>
      <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4">
        <li><strong>Account info:</strong> Email address and password (hashed)</li>
        <li><strong>Transcription data:</strong> The URLs you submit, resulting transcripts, and speaker identification data</li>
        <li><strong>Chat data:</strong> Your questions and AI responses when using the Chat feature</li>
        <li><strong>Usage data:</strong> Which features you use, transcription history, folder organization</li>
        <li><strong>Payment info:</strong> Handled entirely by Paddle (our Merchant of Record) — we never see or store your card details</li>
        <li><strong>Guest users:</strong> IP address (for free tier tracking only)</li>
      </ul>

      <h2 className="text-xl font-semibold mt-8 mb-3">What We Store</h2>
      <p className="text-gray-700 leading-relaxed mb-4">To provide the service, we store:</p>
      <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4">
        <li>Your transcripts and any edits you make</li>
        <li>Speaker identification metadata (names, roles, timestamps)</li>
        <li>Your library organization (folders, saved items)</li>
        <li>Chat conversation history (questions and answers)</li>
        <li>Cached transcripts to speed up repeated requests</li>
      </ul>

      <h2 className="text-xl font-semibold mt-8 mb-3">Bring Your Own Key (BYOK)</h2>
      <p className="text-gray-700 leading-relaxed mb-4">
        You can optionally provide your own API keys for AI providers (OpenAI, Anthropic, Google) to use with the Chat feature. If you do:
      </p>
      <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4">
        <li><strong>Encryption:</strong> Your API keys are encrypted using AES-256-GCM before storage</li>
        <li><strong>Access:</strong> Keys are only decrypted at the moment of use for your requests</li>
        <li><strong>Deletion:</strong> You can delete your stored keys anytime from settings</li>
        <li><strong>Visibility:</strong> We only show the last 4 characters of stored keys for verification</li>
      </ul>

      <h2 className="text-xl font-semibold mt-8 mb-3">Encryption Details</h2>
      <p className="text-gray-700 leading-relaxed mb-4">
        We use bank-grade encryption for sensitive data like API keys. Technical details:
      </p>
      <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4">
        <li><strong>Algorithm:</strong> AES-256-GCM (256-bit Advanced Encryption Standard with Galois/Counter Mode)</li>
        <li><strong>Key derivation:</strong> PBKDF2-HMAC-SHA256 with 100,000 iterations — each user's data is encrypted with a unique derived key</li>
        <li><strong>Nonce handling:</strong> Random 12-byte nonce generated for each encryption operation — the same data encrypted twice produces different ciphertext</li>
        <li><strong>Authentication:</strong> GCM mode includes a 16-byte authentication tag — any tampering is detected on decryption</li>
      </ul>
      <p className="text-gray-700 leading-relaxed mb-4">
        Even if our database were compromised, your API keys cannot be decrypted without the master encryption key, which is stored separately and never committed to code.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-3">How We Use Your Data</h2>
      <p className="text-gray-700 leading-relaxed mb-4">We use your data to:</p>
      <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4">
        <li>Provide the transcription and chat services you're paying for</li>
        <li>Improve speaker identification accuracy over time</li>
        <li>Debug issues when something goes wrong</li>
        <li>Send you important service updates (rarely)</li>
      </ul>
      <p className="text-gray-700 leading-relaxed mb-4">
        We don't sell your data. We don't use it for advertising. Your transcripts are yours.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-3">Where We Store It</h2>
      <p className="text-gray-700 leading-relaxed mb-4">
        Your data is stored on servers hosted by Hetzner in Germany (EU). Backups are stored on Hetzner Storage Box in Finland (EU). Our team has admin access for operational and debugging purposes only — we don't read your transcripts unless you report an issue and ask us to investigate.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-3">How Long We Keep It</h2>
      <p className="text-gray-700 leading-relaxed mb-4">
        Transcripts and chat history are retained until you delete them. You can delete individual transcripts from your library at any time — deletions are immediate and permanent. Backups are retained for 7 days.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-3">Third-Party Services</h2>
      <p className="text-gray-700 leading-relaxed mb-4">We use a few trusted services to make Echonour work:</p>
      <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4">
        <li><strong>AssemblyAI:</strong> Performs audio transcription</li>
        <li><strong>Anthropic (Claude):</strong> Speaker identification and Chat responses</li>
        <li><strong>OpenAI:</strong> Alternative Chat provider (if you choose or BYOK)</li>
        <li><strong>Google:</strong> Alternative Chat provider (if you choose or BYOK)</li>
        <li><strong>Paddle:</strong> Merchant of Record — processes payments, handles tax compliance, and manages refunds</li>
        <li><strong>Resend:</strong> Sends transactional emails</li>
      </ul>
      <p className="text-gray-700 leading-relaxed mb-4">
        Each service has its own privacy policy. We only share the minimum data required for each service to function.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-3">Your Rights</h2>
      <p className="text-gray-700 leading-relaxed mb-4">You can:</p>
      <ul className="list-disc pl-6 space-y-1 text-gray-700 mb-4">
        <li><strong>Access:</strong> View all your stored data in your library</li>
        <li><strong>Export:</strong> Download your transcripts in multiple formats (TXT, SRT, VTT, JSON)</li>
        <li><strong>Delete:</strong> Remove individual transcripts, chat sessions, or your entire account</li>
        <li><strong>Correct:</strong> Edit speaker names and transcript content</li>
      </ul>
      <p className="text-gray-700 leading-relaxed mb-4">
        For data requests we can't handle through the app, email{" "}
        <a href="mailto:support@echonour.com" className="text-blue-600 underline hover:text-blue-800">support@echonour.com</a>
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-3">Cookies</h2>
      <p className="text-gray-700 leading-relaxed mb-4">
        We use minimal cookies — just what's needed for authentication and keeping you logged in. No tracking cookies, no analytics cookies, no third-party ad cookies.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-3">Changes to This Policy</h2>
      <p className="text-gray-700 leading-relaxed mb-4">
        If we make significant changes to how we handle your data, we'll notify you by email before the changes take effect. Minor clarifications won't trigger notifications.
      </p>

      <h2 className="text-xl font-semibold mt-8 mb-3">Contact</h2>
      <p className="text-gray-700 leading-relaxed mb-4">
        Questions about privacy? Email us at{" "}
        <a href="mailto:support@echonour.com" className="text-blue-600 underline hover:text-blue-800">support@echonour.com</a>.
      </p>
    </div>
  );
}
